import React from "react";
import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Layout,
  Divider,
  Input,
  Upload,
  Row,
  Col,
  Radio,
  Button,
  Spin,
  InputNumber,
  Form,
  Tabs,
  TreeSelect,
  Statistic,
  Space,
  Switch,
  DatePicker,
  Select,
} from "antd";
import BraftEditor, { EditorState } from "braft-editor";
import "braft-editor/dist/index.css";
import Base from "../Base";

//redux
import { connect } from "react-redux";
import { addMedia, removeMedia } from "../../utils/redux/actions/Medias";
import { getMedias } from "../../utils/redux/reducers/Medias";
import { getUserInfo } from "../../utils/redux/reducers/User";
import ProductPropertyForm from "./ProductPropertyForm";
import {
  buildSkuFormValues,
  buildSkuPayloadFromForm,
  buildSkuPayloadFromFormOrValues,
  snapshotSkuValuesByLabelFromGroups,
  firstSkuPriceCents,
  totalSkuQuantity,
  getSkuPriceBoundsFromValues,
  formatSkuPriceRangeYuan,
  pickSkuFormValues,
  hasSkuFormPayload,
} from "./productSpecHelpers";
import ProductCommissionForm from "./ProductCommissionForm";
import TranslateBtn from "../../components/product/TranslateBtn";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";
import { Option } from "antd/lib/mentions";
import VideoUpload from "../../components/media/VideoUpload";
import ProductDetailImagesEditor, {
  detailImagesToPaths,
} from "../../components/media/ProductDetailImagesEditor";
import { getUploadedVideoPaths } from "../../utils/helpers/mediaHelpers";
const { TabPane } = Tabs;

function normalizeProductCategoryChildren(cat) {
  if (!cat) return [];
  // Prefer recursively nested sub_categories; `children` is often shallow from the API.
  const raw =
    cat.sub_categories ??
    cat.sub_categories_list ??
    cat.children ??
    cat.subs;
  return Array.isArray(raw) ? raw : [];
}

/** Same semantics as ProductList nestMerchantCategoriesForest (see comment there). */
function nestMerchantProductCategories(list) {
  if (!Array.isArray(list) || list.length === 0) return list;

  const hasDeepEmbeddedTree = list.some((c) =>
    normalizeProductCategoryChildren(c).some(
      (s) => normalizeProductCategoryChildren(s).length > 0
    )
  );
  if (hasDeepEmbeddedTree) return list;

  const getParentId = (c) => c.parent_id ?? c.parentId;
  let usesPointers = false;
  const idSet = new Set(list.map((c) => c.id));
  for (const c of list) {
    const p = getParentId(c);
    if (p != null && p !== 0 && idSet.has(p)) {
      usesPointers = true;
      break;
    }
  }
  if (!usesPointers) return list;

  const byId = new Map();
  list.forEach((raw) => {
    const node = { ...raw };
    delete node.children;
    delete node.sub_categories;
    node.sub_categories = [];
    byId.set(raw.id, node);
  });

  const roots = [];
  byId.forEach((node) => {
    const p = getParentId(node);
    if (p != null && p !== 0 && byId.has(p)) {
      byId.get(p).sub_categories.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function flattenProductCategoriesForLookup(categories) {
  const result = [];
  const walk = (cats) => {
    cats.forEach((c) => {
      result.push(c);
      walk(normalizeProductCategoryChildren(c));
    });
  };
  walk(categories);
  return result;
}

class ProductEdit extends Base {
  static Status = [
    { value: 0, name: "productEdit.status.offShelf" },
    { value: 1, name: "productEdit.status.onShelf" },
  ];

  static Suggestion = [
    { value: 0, name: "productEdit.suggest.off" },
    { value: 1, name: "productEdit.suggest.on" },
  ];

  productForm = React.createRef();
  propertyFormRef = React.createRef();

  /* lifecycle methods */

  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      editProduct: this.getEditProduct(),
      uploadingImages: false,
      updatingProduct: false,
      fetchingImg: null,
      activeKey: "1",
      video: [],
      videoDeleted: false,
      commissions: [],
      selectedLevels: [],
      hasSpecs: false,
      units: [],
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadProductUnits();
    if (this.isEditMode() && this.getEditProduct()) {
      const product = this.getEditProduct();
      const commissions = [];
      const levelIds = product?.commissions?.map((comm) => {
        commissions.push({
          id: comm.agent_level_id,
          value: {
            commission: comm.commission,
            parent_commission: comm.parent_commission,
          },
        });
        return comm.agent_level_id;
      });
      
      this.setState({ commissions, selectedLevels: levelIds });
      this.detectExistingSpecs(product.id);
    }
  }

  async detectExistingSpecs(productId) {
    const token = this.getUserToken();
    if (!token || !productId) return;
    try {
      const res = await this.api.productProperty.list.run({
        token,
        query: { product_id: productId },
      });
      if (res.result && res.data?.length > 0) {
        this.setState({ hasSpecs: true });
        return;
      }

      const skuRes = await this.api.productProperty.skuList.run({
        token,
        query: { product_id: productId },
      });
      if (skuRes.result && skuRes.data?.length > 0) {
        this.setState({ hasSpecs: true });
      }
    } catch (error) {
      console.log("detectExistingSpecs -> error", error);
    }
  }

  async loadProductUnits() {
    if (!this.isMultiUnitEnabled()) return;
    const token = this.getUserToken();
    if (!token) return;
    try {
      const res = await this.api.product.productUnits.run({ token });
      if (res.result) {
        this.setState({ units: res.data || [] });
      }
    } catch (error) {
      console.log("loadProductUnits -> error", error);
    }
  }

  isMultiUnitEnabled() {
    const { settings } = this.props;
    return !!(settings && settings.multi_unit_enabled);
  }

  isUseImageDetail() {
    const { settings } = this.props;
    return !!(settings && settings.use_image_detail);
  }

    applyPackUnitFields(data, values) {
    if (!this.isMultiUnitEnabled()) return;
    data.unit_id = values.unit_id || null;
    if (values.has_pack_unit && values.pack_unit_id && values.pack_size >= 2) {
      data.pack_unit_id = values.pack_unit_id;
      data.pack_size = values.pack_size;
      data.pack_unit_price =
        values.pack_unit_price != null ? Math.round(values.pack_unit_price * 100) : 0;
    } else {
      data.pack_unit_id = null;
      data.pack_size = 0;
      data.pack_unit_price = 0;
    }
  }
  /* render methods */

  setCommissions = (commissions) => {
    this.setState({ commissions });
  };

  setSelectedLevels = (selectedLevels) => {
    this.setState({ selectedLevels });
  };

  render() {
    return (
      <Layout id="route-product-edit">
        {this._renderHeader()}
        {this._renderContent()}
        {this._renderFooter()}
        {this._renderModals()}
      </Layout>
    );
  }

  _renderHeader() {
    let title = this.i18n.t("productEdit.headerTitle");
    if (this.state.editProduct) {
      title = this.i18n.t("productEdit.headerTitleEdit");
    }

    return (
      <Layout.Header className="edit-header">
        <Button
          style={{
            boxShadow: "none",
            borderWidth: 0,
            backgroundColor: "transparent",
          }}
          onClick={this.onHeaderBackClick}
        >
          <LeftOutlined />
          {this.i18n.t("productEdit.headerBack")}
        </Button>
        <Divider type="vertical" />
        <span className="title">{title}</span>
      </Layout.Header>
    );
  }

  _renderContent() {
    const { activeKey } = this.state;

    const items = [
      {
        key: "1",
        label: (
          <div className="product-tab required">
            {this.i18n.t("productEdit.basicInfo")}
          </div>
        ),
        children: (
          <>
            {this._renderContentFormCategory()}
            {this._renderContentFormName()}
            {this._renderContentFormNameUg()}
            {this._renderContentJDImages()}
            {this._renderContentFormImage()}
            {this._renderContentFormVideo()}
            {this._renderContentFormOrder()}
            {this._renderContentFormStatus()}
          </>
        ),
      },
      {
        key: "2",
        forceRender: true,
        label: (
          <div className="product-tab required">
            {this.i18n.t("productEdit.priceInfo")}
          </div>
        ),
        children: (
          <>
            {this._renderMultiSpecSwitch()}
            {this._renderContentFormStandardInPrice()}
            {this._renderContentFormStandardOutPrice()}
            {this._renderContentFormStandardQuantity()}
            {this._renderContentFormStandardShipPrice()}
            {this.renderFressShipppingSwitch()}
            <Divider />
            {this._renderContentFormStandardPackPrice()}
            {this._renderContentFormStandardPackCount()}
            {this._renderContentFormPackUnits()}
            {this.isMultiUnitEnabled()
              ? this._renderContentFormBarcode()
              : null}
            <Divider />
            {this._renderContentFormStandardMaxBuyLimitPrice()}
            {this._renderContentFormStandardMinBuyLimitPrice()}
          </>
        ),
      },
      {
        key: "6",
        label: (
          <div className="product-tab">
            {this.i18n.t("productEdit.otherInfo")}
          </div>
        ),
        children: (
          <>
            {this._renderContentFormDetailDescription()}
            {this._renderContentFormUgDetailDescription()}

            {this.isMultiUnitEnabled()
              ? null
              : this._renderContentFormBarcode()}
            {this._renderContentFormNumber()}
            {this._renderContentFormSuggest()}
            {this.renderAgentLevelProductSwitch()}
          </>
        ),
      },
      {
        key: "3",
        label: (
          <div className="product-tab">
            {this.i18n.t(
              this.isUseImageDetail()
                ? "productEdit.detailImagesLabel"
                : "productEdit.detailLabel"
            )}
          </div>
        ),
        forceRender: this.isUseImageDetail(),
        children: this.isUseImageDetail() ? (
          this._renderContentFormDetailImages()
        ) : (
          <>
            {this.renderUpdateDescBtn()}
            {this._renderContentFormDescription()}
            {this._renderContentFormDescriptionUg()}
          </>
        ),
      },
    ];

    if (this.merchantHasCommission()) {
      items.push({
        label: (
          <div className="product-tab">
            {this.i18n.t("productEdit.commissionInfo")}
          </div>
        ),
        key: "4",
        forceRender: true,
      });
    }

    if (this.state.hasSpecs || this.isEditMode()) {
      items.push({
        label: (
          <div className="product-tab">
            {this.i18n.t("productEdit.propertyLabel")}
          </div>
        ),
        key: "5",
        forceRender: true,
      });
    }

    return (
      <Layout.Content className="edit-content">
        <Spin
          spinning={this.state.uploadingImages || this.state.updatingProduct}
        >
          <Form ref={this.productForm} preserve>
            <Tabs
              className="product-tabs"
              defaultActiveKey="1"
              onChange={this.onTabChange}
              activeKey={activeKey}
              items={items}
            />
            {this._renderContentSkuImage()}
            {this._renderContentFormProperty()}
          </Form>
        </Spin>
      </Layout.Content>
    );
  }

  _renderContentFormCategory() {
    if (this.isJdShop()) return null;
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.catLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 8 },
      required: true,
    };
    let categories = this.getCategories();
    const mapToTreeNodes = (list) =>
      list.map((i) => {
        const subs = normalizeProductCategoryChildren(i);
        const node = {
          title: this.i18n.getPropNameByLocale(i, "name"),
          value: `${i.id}`,
        };
        if (subs.length > 0) {
          node.children = mapToTreeNodes(subs);
        }
        return node;
      });
    let treeData =
      Array.isArray(categories) && categories.length > 0
        ? mapToTreeNodes(categories)
        : [];
    let currentCat = this.getDefaultCategory();
    const categorySelector = (
      <TreeSelect treeData={treeData} treeDefaultExpandAll />
    );
    return (
      <Form.Item
        {...item}
        name="category_id"
        rules={[
          {
            required: true,
            message: this.i18n.t("productEdit.catRequired"),
          },
        ]}
        initialValue={currentCat}
      >
        {categorySelector}
      </Form.Item>
    );
  }

  _renderContentFormNumber() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.numberLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 8 },
      required: false,
    };
    return (
      <Form.Item
        {...item}
        name="product_no"
        initialValue={this.getDefaultNumber()}
        rules={[
          {
            required: false,
            message: this.i18n.t("productEdit.numberRequired"),
          },
        ]}
      >
        <Input placeholder={this.i18n.t("productEdit.numberHint")} />
      </Form.Item>
    );
  }

  _renderContentFormValidDate() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.valid_date"),
      labelCol: { span: 15 },
      wrapperCol: { span: 20 },
      required: false,
    };

    const item1 = {
      labelCol: { span: 5 },
      wrapperCol: { span: 25 },
      required: false,
    };

    return (
      <div style={{ display: "flex" }}>
        <Form.Item
          {...item}
          name="valid_date"
          initialValue={this.getDefaultValidDate()}
          rules={[
            {
              required: false,
              message: this.i18n.t("productEdit.numberRequired"),
            },
          ]}
        >
          <Input placeholder={this.i18n.t("productEdit.validHint")} />
        </Form.Item>
        <Form.Item
          {...item1}
          name="validUnion"
          rules={[
            {
              required: false,
              message: this.i18n.t("productEdit.numberRequired"),
            },
          ]}
        >
          <Select
            onSelect={(value) =>
              this.productForm.current.setFieldsValue({
                validUnion: value,
              })
            }
            defaultValue="日"
          >
            <Option value="日">日</Option>
            <Option value="月">月</Option>
            <Option value="年">年</Option>
          </Select>
        </Form.Item>
      </div>
    );
  }

  _renderContentFormManufacturedDate() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.manufactured_date"),
      labelCol: { span: 4 },
      wrapperCol: { span: 8 },
      required: false,
    };
    return (
      <Form.Item
        {...item}
        name="manufactured_date"
        rules={[
          {
            required: false,
            message: this.i18n.t("productEdit.numberRequired"),
          },
        ]}
      >
        <DatePicker
          format="YYYY-MM-DD"
          allowClear={false}
          onChange={() => console.log("==>")}
        />
      </Form.Item>
    );
  }

  _renderContentFormRecommendedPrice() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.recommended_price"),
      labelCol: { span: 4 },
      wrapperCol: { span: 8 },
      required: false,
    };
    return (
      <Form.Item
        {...item}
        name="recommended_price"
        initialValue={this.getDefaultRecommendedPrice()}
        rules={[
          {
            required: false,
            message: this.i18n.t("productEdit.numberRequired"),
          },
        ]}
      >
        <Input placeholder={this.i18n.t("productEdit.recommendedHint")} />
      </Form.Item>
    );
  }

  _renderContentFormBasicSales() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.basic_sales"),
      labelCol: { span: 4 },
      wrapperCol: { span: 8 },
      required: false,
    };
    return (
      <Form.Item
        {...item}
        name="basic_sales"
        initialValue={this.getDefaultBasicSales()}
        rules={[
          {
            required: false,
            message: this.i18n.t("productEdit.numberRequired"),
          },
        ]}
      >
        <Input placeholder={this.i18n.t("productEdit.basicHint")} />
      </Form.Item>
    );
  }
  _renderUgSquareImage() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.ugImagesLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
    };
    if (!this.shopHasAgents()) {
      return null;
    }
    return (
      <>
        <Form.Item
          label={this.i18n.t("productEdit.zhImagesLabel")}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 16 }}
        >
          <img
            width={150}
            height={150}
            src={this.getEditProduct()?.zh_four_square_image}
          />
        </Form.Item>
        <Form.Item
          label={this.i18n.t("productEdit.ugImagesLabel")}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 16 }}
        >
          <img
            width={150}
            height={150}
            src={this.getEditProduct()?.ug_four_square_image}
          />
        </Form.Item>
      </>
    );
  }
  _renderContentFormBarcode() {
    const labelCol = { span: 4 };
    const wrapperCol = { span: 8 };

    if (!this.isMultiUnitEnabled()) {
      return (
        <Form.Item
          colon={false}
          label={this.i18n.t("productEdit.barcodeLabel")}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          name="barcode"
          initialValue={this.getDefaultBarcode()}
          rules={[{ required: false }]}
        >
          <Input placeholder={this.i18n.t("productEdit.barcodeHint")} />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.has_pack_unit !== cur.has_pack_unit}
      >
        {() => {
          const hasPack = !!this.productForm.current?.getFieldValue(
            "has_pack_unit"
          );
          return (
            <>
              <Form.Item
                colon={false}
                label={this.i18n.t("productEdit.baseUnitBarcodeLabel")}
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                name="barcode"
                initialValue={this.getDefaultBarcode()}
                rules={[{ required: false }]}
              >
                <Input
                  placeholder={this.i18n.t("productEdit.baseUnitBarcodeHint")}
                />
              </Form.Item>
              {hasPack ? (
                <Form.Item
                  colon={false}
                  label={this.i18n.t("productEdit.packUnitBarcodeLabel")}
                  labelCol={labelCol}
                  wrapperCol={wrapperCol}
                  name="pack_barcode"
                  initialValue={this.getDefaultPackBarcode()}
                  rules={[{ required: false }]}
                >
                  <Input
                    placeholder={this.i18n.t(
                      "productEdit.packUnitBarcodeHint"
                    )}
                  />
                </Form.Item>
              ) : null}
            </>
          );
        }}
      </Form.Item>
    );
  }

  _renderContentFormName() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.nameLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
      required: true,
    };

    return (
      <Form.Item noStyle shouldUpdate>
        {() => (
          <Form.Item
            {...item}
            name="zh_name"
            initialValue={this.getDefaultName()}
            rules={[
              {
                required: true,
                message: this.i18n.t("productEdit.nameRequired"),
              },
            ]}
          >
            <Input
              disabled={this.hasEditProductSku()}
              placeholder={this.i18n.t("productEdit.nameHint")}
              allowClear
              addonAfter={
                <TranslateBtn
                  onFinish={(value) =>
                    this.productForm.current.setFieldsValue({
                      ug_name: value,
                    })
                  }
                  context={
                    this.productForm.current &&
                    this.productForm.current.getFieldValue("zh_name")
                  }
                  from="zh"
                  to="ug"
                />
              }
            />
          </Form.Item>
        )}
      </Form.Item>
    );
  }

  _renderContentFormNameUg() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.nameUgLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
    };
    return (
      <Form.Item noStyle shouldUpdate>
        {() => (
          <Form.Item
            {...item}
            name="ug_name"
            initialValue={this.getDefaultUgName()}
          >
            <Input
              disabled={this.hasEditProductSku()}
              placeholder={this.i18n.t("productEdit.nameUgHint")}
              dir="rtl"
              allowClear
              addonAfter={
                <TranslateBtn
                  onFinish={(value) =>
                    this.productForm.current.setFieldsValue({
                      zh_name: value,
                    })
                  }
                  context={
                    this.productForm.current &&
                    this.productForm.current.getFieldValue("ug_name")
                  }
                  from="ug"
                  to="zh"
                />
              }
            />
          </Form.Item>
        )}
      </Form.Item>
    );
  }

  _renderContentFormDetailDescription() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.detailDescLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
    };

    return (
      <Form.Item noStyle shouldUpdate>
        {() => (
          <Form.Item
            {...item}
            name="zh_detail_description"
            initialValue={this.getDetailDesc()}
          >
            <Input
              disabled={this.hasEditProductSku()}
              placeholder={this.i18n.t("productEdit.detailDescLabelHint")}
              allowClear
              addonAfter={
                <TranslateBtn
                  context={
                    this.productForm.current &&
                    this.productForm.current.getFieldValue(
                      "zh_detail_description"
                    )
                  }
                  onFinish={(value) =>
                    this.productForm.current.setFieldsValue({
                      ug_detail_description: value,
                    })
                  }
                  from="zh"
                  to="ug"
                />
              }
            />
          </Form.Item>
        )}
      </Form.Item>
    );
  }

  _renderContentFormUgDetailDescription() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.detailDescUgLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
    };

    return (
      <Form.Item noStyle shouldUpdate>
        {() => (
          <Form.Item
            {...item}
            name="ug_detail_description"
            initialValue={this.getDetailUgDesc()}
          >
            <Input
              disabled={this.hasEditProductSku()}
              placeholder={this.i18n.t("productEdit.detailUgDescLabelHint")}
              dir="rtl"
              allowClear
              addonAfter={
                <TranslateBtn
                  context={
                    this.productForm.current &&
                    this.productForm.current.getFieldValue(
                      "ug_detail_description"
                    )
                  }
                  onFinish={(value) =>
                    this.productForm.current.setFieldsValue({
                      zh_detail_description: value,
                    })
                  }
                  from="ug"
                  to="zh"
                />
              }
            />
          </Form.Item>
        )}
      </Form.Item>
    );
  }

  _renderContentFormVideo() {
    if (this.isBBLbsShop() || !this.shopHasAgents()) {
      return null;
    }
    return (
      <Form.Item shouldUpdate noStyle>
        {({ getFieldValue }) => {
          const video = getFieldValue("video");
          const item = {
            colon: false,
            label: this.i18n.t("productEdit.video"),
            labelCol: { span: 4 },
            wrapperCol: { span: 16 },
            required: false,
          };
          return (
            <Form.Item
              {...item}
              name="video"
              initialValue={this.getDefaultVideo()}
            >
              <Input hidden />
              <VideoUpload
                video={this.state.videoDeleted ? null : this.getDefaultVideo()}
                setSelectedVideo={(video) => {
                  this.productForm.current.setFieldsValue({ video });
                  this.setState({ videoDeleted: false });
                }}
                setDeleted={() => {
                  this.setState({ videoDeleted: true });
                }}
              />
            </Form.Item>
          );
        }}
      </Form.Item>
    );
  }

  _renderContentFormImage() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.imagesLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
      required: true,
    };
    return (
      <Form.Item
        {...item}
        name="images"
        valuePropName="fileList"
        initialValue={this.getDefaultImages()}
        rules={[
          {
            required: true,
            message: this.i18n.t("productEdit.imagesRequired"),
          },
        ]}
        getValueFromEvent={this.onUploadImageChange}
      >
        <Upload
          accept="image/*"
          action=""
          listType="picture-card"
          multiple
          beforeUpload={this.onUploadImageBefore}
          onPreview={this.onUploadImagePreview}
          onRemove={this.onUploadImageRemove}
          openFileDialogOnClick
        >
          {!this.hasEditProductSku() && (
            <div>
              <PlusOutlined />
              <div className="ant-upload-text">
                {this.i18n.t("productEdit.imagesUpload")}
              </div>
            </div>
          )}
        </Upload>
      </Form.Item>
    );
  }

  _renderContentJDImages() {
    if (this.isJdShop()) {
      return (
        <Form.Item
          label={this.i18n.t("productEdit.refetchImgs")}
          labelCol={{ span: this.i18n.isLocaleRTL() ? 6 : 4 }}
          wrapperCol={{ span: this.i18n.isLocaleRTL() ? 16 : 8 }}
        >
          <Button
            type="primary"
            onClick={this.getJdProductImg}
            disabled={this.state.fetchingImg === "img"}
            loading={this.state.fetchingImg === "img"}
          >
            {this.i18n.t("productEdit.refetchImgs")}
          </Button>
        </Form.Item>
      );
    }
    return null;
  }

  renderUpdateDescBtn() {
    if (this.isJdShop()) {
      return (
        <Form.Item
          label={this.i18n.t("productEdit.refetchDesc")}
          labelCol={{ span: this.i18n.isLocaleRTL() ? 6 : 4 }}
          wrapperCol={{ span: this.i18n.isLocaleRTL() ? 16 : 8 }}
        >
          <Button
            type="primary"
            onClick={this.getJdProductDescription}
            disabled={this.state.fetchingImg === "disc"}
            loading={this.state.fetchingImg === "disc"}
          >
            {this.i18n.t("productEdit.refetchDesc")}
          </Button>
        </Form.Item>
      );
    }
    return null;
  }

  _renderContentFormDetailImages() {
    return (
      <Form.Item
        colon={false}
        label={this.i18n.t("productEdit.detailImagesLabel")}
        labelCol={{ span: this.i18n.isLocaleRTL() ? 6 : 4 }}
        wrapperCol={{ span: 16 }}
        name="detail_images"
        initialValue={this.getDefaultDetailImages()}
      >
        <ProductDetailImagesEditor />
      </Form.Item>
    );
  }

  _renderContentFormDescription() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.descLabel"),
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: 16 },
    };

    return (
      <Form.Item
        {...item}
        name="zh_description"
        initialValue={this.getDefaultDescription()}
      >
        <BraftEditor
          media={{
            items: this.props.images,
            validateFn: this.onDescriptionMediaValidate,
            uploadFn: this.onDescriptionMediaUpload,
          }}
          hooks={{
            "remove-medias": this.onDescriptionMediaRemove,
          }}
        />
      </Form.Item>
    );
  }

  _renderContentFormDescriptionUg() {
    if (this.isBBLbsShop()) {
      return null;
    }
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.descUgLabel"),
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: 16 },
    };

    return (
      <Form.Item
        {...item}
        name="ug_description"
        initialValue={this.getDefaultUgDescription()}
      >
        <BraftEditor
          media={{
            items: this.props.images,
            validateFn: this.onDescriptionMediaValidate,
            uploadFn: this.onDescriptionMediaUpload,
          }}
          hooks={{
            "remove-medias": this.onDescriptionMediaRemove,
          }}
        />
      </Form.Item>
    );
  }

  _renderContentFormStandard() {
    return (
      <Row className="ant-form-item ant-form-item-no-colon standard">
        <Col
          span={4}
          className="ant-form-item-label ant-legacy-form-item-label"
        >
          <label className="ant-form-item-required ant-legacy-form-item-no-colon">
            {this.i18n.t("productEdit.standardLabel")}
          </label>
        </Col>
        <Col span={16} className="ant-form-item-control-wrapper standard">
          {this._renderContentFormStandardInPrice()}
          {this._renderContentFormStandardOutPrice()}
          {this._renderContentFormStandardCommission()}
          {this._renderContentFormStandardParentCommission()}
          {this._renderContentFormStandardQuantity()}
          {this._renderContentFormPackUnits()}
          {this.isMultiUnitEnabled()
            ? this._renderContentFormBarcode()
            : null}
          {this._renderContentFormStandardPackPrice()}
          {this._renderContentFormStandardPackCount()}
        </Col>
      </Row>
    );
  }

  _renderContentFormProperty() {
    const { activeKey, hasSpecs } = this.state;
    const product = this.getEditProduct();

    return (
      <>
        {this.merchantHasCommission() && (
          <div style={{ display: activeKey === "4" ? "block" : "none" }}>
            <ProductCommissionForm
              product={product}
              setCommissions={this.setCommissions}
              commissions={this.state.commissions}
              form={this.productForm.current}
              setSelectedLevels={this.setSelectedLevels}
            />
          </div>
        )}
        {(this.state.hasSpecs || this.isEditMode()) && (
          <div style={{ display: activeKey === "5" ? "block" : "none" }}>
            <ProductPropertyForm
              key={
                this.isCloneMode()
                  ? `clone-${product?.id || "new"}`
                  : `edit-${product?.id || "new"}`
              }
              ref={this.propertyFormRef}
              product={product}
              parentForm={this.productForm}
              cloneMode={this.isCloneMode()}
            />
          </div>
        )}
      </>
    );
  }

  _renderContentFormStandardInPrice() {
    if (this.isBBLbsShop()) {
      return null;
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.inPriceLabel"),
      required: true,
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="purchase_price"
        initialValue={this.getDefaultInPrice()}
        rules={[
          {
            required: true,
            message: this.i18n.t("productEdit.standardRequired"),
          },
        ]}
      >
        <InputNumber min={0} step={1} formatter={(value) => `￥${value}`} />
      </Form.Item>
    );
  }

  renderFressShipppingSwitch() {
    if (this.isBBLbsShop()) {
      return null;
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.isFreeShipping"),
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="is_free_shipping"
        initialValue={this.getDefaultFreeShipping()}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    );
  }

  renderMultiSpecSwitch() {
    if (this.isJdShop()) return null;

    return (
      <Form.Item
        colon={false}
        label={this.i18n.t("productEdit.multiSpecProduct")}
        className="item-input"
        labelCol={{ span: this.i18n.isLocaleRTL() ? 6 : 4 }}
        wrapperCol={{ span: this.i18n.isLocaleRTL() ? 16 : 8 }}
      >
        <Switch
          checked={this.state.hasSpecs}
          onChange={(checked) => {
            this.setState({ hasSpecs: checked, activeKey: checked ? "5" : this.state.activeKey });
          }}
        />
        <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
          {this.i18n.t("productEdit.multiSpecProductHint")}
        </div>
      </Form.Item>
    );
  }

  _renderMultiSpecSwitch() {
    return this.renderMultiSpecSwitch();
  }
  renderAgentLevelProductSwitch() {
    if (!this.isAgentLevelProductEnabled()) {
      return null;
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.isAgentLevelProduct"),
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="is_agent_level_product"
        initialValue={this.getDefaultAgentLevelProduct()}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    );
  }

  _renderContentFormStandardShipPrice() {
    if (this.isBBLbsShop()) {
      return null;
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.shipPriceLabel"),
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => (
          <Form.Item
            {...item}
            name="ship_price"
            initialValue={this.getDefaultShipPrice()}
          >
            <InputNumber
              disabled={getFieldValue("is_free_shipping")}
              min={0}
              step={1}
              formatter={(value) => `￥${value}`}
            />
          </Form.Item>
        )}
      </Form.Item>
    );
  }

  _renderContentFormStandardMinBuyLimitPrice() {
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.minBuyLimitLabel"),
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="min_buy_limit"
        initialValue={this.getDefaultMinBuyLimit()}
      >
        <InputNumber min={0} step={1} />
      </Form.Item>
    );
  }

  _renderContentFormStandardMaxBuyLimitPrice() {
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.maxBuyLimitLabel"),
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="max_buy_limit"
        initialValue={this.getDefaultMaxBuyLimit()}
      >
        <InputNumber min={0} step={1} />
      </Form.Item>
    );
  }

  _renderContentFormStandardOutPrice() {
    if (this.state.hasSpecs) {
      return (
        <Form.Item
          colon={false}
          label={this.i18n.t("productEdit.outPriceLabel")}
          labelCol={{ span: this.i18n.isLocaleRTL() ? 6 : 4 }}
          wrapperCol={{ span: this.i18n.isLocaleRTL() ? 16 : 8 }}
          shouldUpdate
        >
          {() => {
            const { groups, skuList, isZh } = this.getSpecContext();
            const values =
              this.productForm.current?.getFieldsValue(true) || {};
            const bounds = getSkuPriceBoundsFromValues(
              groups,
              skuList,
              values,
              isZh
            );
            const rangeText = formatSkuPriceRangeYuan(bounds);

            return (
              <div className="multi-spec-price-range">
                {rangeText ? (
                  <span className="multi-spec-price-range__value">
                    {this.i18n.t("productEdit.multiSpecPriceRange")}:{" "}
                    {rangeText}
                  </span>
                ) : (
                  <span style={{ color: "#888" }}>
                    {this.i18n.t("productEdit.multiSpecPriceHint")}
                  </span>
                )}
              </div>
            );
          }}
        </Form.Item>
      );
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.outPriceLabel"),
      required: true,
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="price"
        initialValue={this.getDefaultOutPrice()}
        rules={[
          {
            required: true,
            message: this.i18n.t("productEdit.standardRequired"),
          },
        ]}
      >
        <InputNumber min={0} step={1} formatter={(value) => `￥${value}`} />
      </Form.Item>
    );
  }

  _renderContentFormStandardCommission() {
    if (this.merchantHasCommission()) {
      let item = {
        colon: true,
        label: this.i18n.t("productEdit.commission"),
        required: false,
        className: "item-input",
        labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
        wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
      };

      return (
        <Form.Item
          {...item}
          name="commission_percent"
          initialValue={this.getDefaultCommission()}
          rules={[
            {
              required: false,
              message: this.i18n.t("productEdit.standardRequired"),
            },
          ]}
        >
          <InputNumber min={0} step={1} formatter={(value) => `¥${value}`} />
        </Form.Item>
      );
    }
    return null;
  }

  _renderContentFormStandardParentCommission() {
    if (
      this.isBBShopMerchant() ||
      this.isAdminWithDabazaShop() ||
      (this.isThirdPartyShopHasWeapp() && this.shopHasAgents())
    ) {
      let item = {
        colon: true,
        label: this.i18n.t("productEdit.parentCommission"),
        required: false,
        className: "item-input",
        labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
        wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
      };

      return (
        <Form.Item
          {...item}
          name="parent_commission_percent"
          initialValue={this.getDefaultParentCommission()}
          rules={[
            {
              required: false,
              message: this.i18n.t("productEdit.standardRequired"),
            },
          ]}
        >
          <InputNumber min={0} step={1} formatter={(value) => `¥${value}`} />
        </Form.Item>
      );
    }

    return null;
  }

  _renderContentFormStandardQuantity() {
    if (this.state.hasSpecs) {
      return null;
    }
    let item = {
      colon: false,
      label: this.i18n.t("productEdit.quantityLabel"),
      required: true,
      className: "item-input",
      labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
      wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
    };

    return (
      <Form.Item
        {...item}
        name="quantity"
        initialValue={this.getDefaultQuantity()}
        rules={[
          {
            required: true,
            message: this.i18n.t("productEdit.standardRequired"),
          },
        ]}
      >
        <InputNumber min={0} step={1} />
      </Form.Item>
    );
  }

  _renderContentFormStandardPackPrice() {
    if (this.isLbsShop() || this.isBBLbsShop()) {
      let item = {
        colon: false,
        label: this.i18n.t("productEdit.packPriceLabel"),
        className: "item-input",
        labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
        wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
      };

      return (
        <Form.Item
          {...item}
          name="pack_price"
          initialValue={this.getDefaultPackPrice()}
        >
          <InputNumber min={0} step={1} formatter={(value) => `￥${value}`} />
        </Form.Item>
      );
    }
    return null;
  }

  _renderContentFormStandardPackCount() {
    if (this.isLbsShop() || this.isBBLbsShop()) {
      let item = {
        colon: false,
        label: this.i18n.t("productEdit.packCountLabel"),
        className: "item-input",
        labelCol: { span: this.i18n.isLocaleRTL() ? 6 : 4 },
        wrapperCol: { span: this.i18n.isLocaleRTL() ? 16 : 8 },
      };
      return (
        <Form.Item
          {...item}
          name="pack_count"
          initialValue={this.getDefaultPackCount()}
        >
          <InputNumber min={0} step={1} />
        </Form.Item>
      );
    }
    return null;
  }

  _renderContentFormPackUnits() {
    if (!this.isMultiUnitEnabled()) {
      return null;
    }
    const { units } = this.state;
    const labelCol = { span: this.i18n.isLocaleRTL() ? 6 : 4 };
    const wrapperCol = { span: this.i18n.isLocaleRTL() ? 16 : 8 };
    const unitOptions = (units || []).map((unit) => ({
      value: unit.id,
      label: this.i18n.getPropNameByLocale(unit, "name"),
    }));
    const rowStyle = {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      direction: this.i18n.isLocaleRTL() ? "rtl" : "ltr",
    };

    return (
      <>
        <Divider />
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.has_pack_unit !== cur.has_pack_unit ||
            prev.unit_id !== cur.unit_id
          }
        >
          {() => {
            const hasPack = this.productForm.current?.getFieldValue(
              "has_pack_unit"
            );
            const baseUnitId = this.productForm.current?.getFieldValue(
              "unit_id"
            );
            const baseUnit = (units || []).find((u) => u.id === baseUnitId);
            const baseUnitLabel = baseUnit
              ? this.i18n.getPropNameByLocale(baseUnit, "name")
              : "";
            const packOptions = unitOptions.filter(
              (opt) => opt.value !== baseUnitId
            );

            return (
              <Form.Item
                colon={false}
                label={this.i18n.t("productEdit.baseUnitLabel")}
                className="item-input"
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                extra={
                  hasPack ? this.i18n.t("productEdit.packUnitHint") : null
                }
              >
                <div>
                  <div style={rowStyle}>
                    <Form.Item
                      name="unit_id"
                      initialValue={this.getDefaultUnitId()}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        allowClear
                        style={{ minWidth: 120 }}
                        placeholder={this.i18n.t("productEdit.selectUnit")}
                        options={unitOptions}
                        notFoundContent={this.i18n.t(
                          "productEdit.manageUnitsHint"
                        )}
                        onChange={(value) => {
                          const packUnitId =
                            this.productForm.current?.getFieldValue(
                              "pack_unit_id"
                            );
                          if (value && packUnitId && value === packUnitId) {
                            this.productForm.current?.setFieldsValue({
                              pack_unit_id: undefined,
                            });
                          }
                          if (!value) {
                            this.productForm.current?.setFieldsValue({
                              has_pack_unit: false,
                              pack_unit_id: undefined,
                              pack_size: undefined,
                            });
                          }
                        }}
                      />
                    </Form.Item>
                    <span>{this.i18n.t("productEdit.hasPackUnit")}</span>
                    <Form.Item
                      name="has_pack_unit"
                      valuePropName="checked"
                      initialValue={this.getDefaultHasPackUnit()}
                      style={{ marginBottom: 0 }}
                    >
                      <Switch
                        onChange={(checked) => {
                          if (!checked) {
                            this.productForm.current?.setFieldsValue({
                              pack_unit_id: undefined,
                              pack_size: undefined,
                              pack_barcode: undefined,
                            });
                            return;
                          }
                          const selectedBase =
                            this.productForm.current?.getFieldValue("unit_id");
                          if (!selectedBase) {
                            this.showMessage(
                              this.i18n.t("productEdit.baseUnitRequired"),
                              Base.Message.warning
                            );
                            setTimeout(() => {
                              this.productForm.current?.setFieldsValue({
                                has_pack_unit: false,
                              });
                            }, 0);
                          }
                        }}
                      />
                    </Form.Item>
                  </div>
                  {hasPack && baseUnitId ? (
                    <div style={{ ...rowStyle, marginTop: 8 }}>
                      <span>1 ×</span>
                      <Form.Item
                        name="pack_unit_id"
                        initialValue={this.getDefaultPackUnitId()}
                        rules={[
                          {
                            required: true,
                            message: this.i18n.t("productEdit.selectUnit"),
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          style={{ minWidth: 120 }}
                          placeholder={this.i18n.t("productEdit.selectUnit")}
                          options={packOptions}
                        />
                      </Form.Item>
                      <span>=</span>
                      <Form.Item
                        name="pack_size"
                        initialValue={this.getDefaultPackSize()}
                        rules={[
                          {
                            required: true,
                            message: this.i18n.t(
                              "productEdit.standardRequired"
                            ),
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          min={2}
                          step={1}
                          style={{ width: 90 }}
                        />
                      </Form.Item>
                      {baseUnitLabel ? <span>{baseUnitLabel}</span> : null}
                    </div>
                  ) : null}
                  {hasPack && baseUnitId ? (
                    <Form.Item
                      name="pack_unit_price"
                      label={this.i18n.t("productEdit.packUnitPriceLabel")}
                      initialValue={this.getDefaultPackUnitPrice()}
                      rules={[
                        {
                          required: true,
                          message: this.i18n.t("productEdit.standardRequired"),
                        },
                      ]}
                      style={{ marginTop: 8, marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: 160 }}
                        formatter={(value) => `￥${value}`}
                      />
                    </Form.Item>
                  ) : null}
                </div>
              </Form.Item>
            );
          }}
        </Form.Item>
      </>
    );
  }

  _renderContentFormStatus() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.statusLabel"),
      labelCol: { span: 4 },
    };
    return (
      <Row>
        <Col span={24}>
          <Form.Item
            {...item}
            name="status"
            initialValue={this.getDefaultStatus()}
          >
            <Radio.Group>
              {ProductEdit.Status.map((status, index) => {
                return (
                  <Radio.Button key={`status-${index}`} value={status.value}>
                    {this.i18n.t(status.name)}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    );
  }

  _renderContentFormOrder() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.orderLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 2 },
    };
    return (
      <Form.Item {...item} name="order" initialValue={this.getDefaultOrder()}>
        <InputNumber min={0} step={1} />
      </Form.Item>
    );
  }

  _renderContentFormSuggest() {
    const item = {
      colon: false,
      label: this.i18n.t("productEdit.suggestLabel"),
      labelCol: { span: 4 },
      wrapperCol: { span: 16 },
    };
    return (
      <Form.Item
        {...item}
        name="recommend"
        initialValue={this.getDefaultSuggestion()}
      >
        <Radio.Group>
          {ProductEdit.Suggestion.map((suggest, index) => {
            return (
              <Radio key={`suggest-${index}`} value={suggest.value}>
                {this.i18n.t(suggest.name)}
              </Radio>
            );
          })}
        </Radio.Group>
      </Form.Item>
    );
  }

  _renderContentSkuImage() {
    if (this.hasEditProductSku()) {
      return (
        <img
          className="sku-img"
          alt=""
          src={process.env.PUBLIC_URL + this.i18n.t("productEdit.skuImage")}
        />
      );
    }

    return null;
  }

  _renderFooter() {
    return (
      <Layout.Footer className="edit-footer">
        <Button type="primary" onClick={() => this.onFooterConfirmClick(false)}>
          {this.i18n.t("productEdit.saveAndBack")}
        </Button>
        {this.isEditMode() && (
          <Button
            type="primary"
            onClick={() => this.onFooterConfirmClick(false, false)}
          >
            {this.i18n.t("productEdit.save")}
          </Button>
        )}
        {!this.isEditMode() ? (
          <Button onClick={() => this.onFooterConfirmClick(true)}>
            {this.i18n.t("productEdit.saveAndContinue")}
          </Button>
        ) : null}
        {this.isJdShop() && this.isEditMode() && (
          <Button type="primary" onClick={this.onFooterDeleteClick}>
            {this.i18n.t("financeDownload.historyItemActionDelete")}
          </Button>
        )}
        <Button onClick={this.onFooterCancelClick}>
          {this.i18n.t("productEdit.cancel")}
        </Button>
      </Layout.Footer>
    );
  }

  _renderModals() {
    let items = [];
    items.push(this.renderImagePreviewModal());
    return items;
  }

  /* user methods */

  onTabChange = (key) => {
    this.setState({ activeKey: key });
  };

  onHeaderBackClick = () => {
    this.goToProductList();
  };

  onUploadImageBefore = (file, fileList) => {
    if (typeof file.uid === "string") {
      if (!this.isImageTypeValid(file)) {
        this.showMessage(
          this.i18n.t("productEdit.uploadImageTypeWarning"),
          Base.Message.warning
        );
      }

      if (!this.isImageLessThan1MB(file)) {
        this.showMessage(
          this.i18n.t("productEdit.uploadImageSizeWarning"),
          Base.Message.warning
        );
      }
    }

    return false;
  };

  onUploadImageChange = (e) => {
    let arr = null;
    if (Array.isArray(e)) {
      arr = e;
    } else {
      arr = e && e.fileList;
    }
    let fileList = [];
    for (let i = 0; i < arr.length; i++) {
      let file = arr[i];
      if (typeof file.uid === "string") {
        if (!this.isImageTypeValid(file.originFileObj)) continue;
        if (!this.isImageLessThan1MB(file.originFileObj)) continue;
      }

      fileList.push(file);
    }
    return fileList;
  };

  onUploadImagePreview = (file) => {
    let fileList = this.productForm.current.getFieldValue("images");
    let fileIndex = fileList.findIndex((i) => file.uid === i.uid);
    this.setState({ previewImages: fileList, previewImageIndex: fileIndex });
    if (this.isJdShop()) {
      this.productForm.current.setFieldsValue({
        images: [...new Set([file, ...fileList])],
      });
    }
  };

  onUploadImageRemove = (file) => {
    if (this.hasEditProductSku()) {
      this.showMessage(
        this.i18n.t("productEdit.skuWarning"),
        Base.Message.warning
      );
      return false;
    }
    return true;
  };

  onUploadVideoBefore = (file) => {
    if (typeof file.uid === "string") {
      if (!this.isVideoLessThan10MB(file)) {
        this.showMessage(
          this.i18n.t("productEdit.uploadVideoSizeWarning"),
          Base.Message.warning
        );
      }
    }

    return false;
  };

  onUploadVideoPreview = () => {
    let fileList = this.editShowForm.current.getFieldValue("video");
    this.setState({
      previewImages: fileList,
      previewImageIndex: 0,
      isImg: false,
    });
  };

  onDescriptionMediaValidate = (file) => {
    if (!this.isImageTypeValid(file)) {
      this.showMessage(
        this.i18n.t("productEdit.uploadImageTypeWarning"),
        Base.Message.warning
      );
      return false;
    }

    if (!this.isImageLessThan1MB(file)) {
      this.showMessage(
        this.i18n.t("productEdit.uploadImageSizeWarning"),
        Base.Message.warning
      );
      return false;
    }

    return true;
  };

  onDescriptionMediaUpload = async (param) => {
    let token = this.getUserToken();
    if (!token) {
      param.error({ msg: "no user access token" });
      return;
    }

    let apiConfig = {
      token: token,
      uploadFiles: [{ file: param.file }],
      uploadType: "images",
    };

    try {
      let res = await this.api.common.uploadImages.run(apiConfig);
      if (res.result) {
        let data = res.data;

        let mediaItem = {
          type: "IMAGE",
          url: data[0].url,
        };

        this.props.addMedia(mediaItem);
      } else {
        param.error({ msg: "upload image error" });
      }
    } catch (error) {
      param.error({ msg: "upload image error" });
    }
  };

  onDescriptionMediaRemove = (href, target) => {
    this.props.removeMedia(href);
  };

  buildAgentLevelCommissionPayload(values, selectedLevels) {
    const payload = { selected_levels: selectedLevels || [] };
    if (!this.merchantHasCommission() || !selectedLevels?.length) {
      return payload;
    }

    selectedLevels.forEach((levelId) => {
      const levelData = values.commissions?.[levelId];
      const commission =
        levelData?.commission ?? values[`commissions[${levelId}][commission]`];
      const parentCommission =
        levelData?.parent_commission ??
        values[`commissions[${levelId}][parent_commission]`];

      if (commission !== undefined && commission !== null) {
        payload[`commissions[${levelId}][commission]`] = commission;
      }
      if (parentCommission !== undefined && parentCommission !== null) {
        payload[`commissions[${levelId}][parent_commission]`] =
          parentCommission;
      }
    });

    return payload;
  }

  onFooterConfirmClick = (continueCreate = false, close = true) => {
    this.productForm.current
      .validateFields()
      .then(async () => {
        const values = this.productForm.current.getFieldsValue(true);
        const hasSpecs = this.effectiveHasSpecs(values);
        if (hasSpecs) {
          const specValidation = this.validateSpecsBeforeSave();
          if (!specValidation.valid) {
            this.showMessage(specValidation.message, Base.Message.error);
            return;
          }
        }
        // Clone is "create with copied fields", never update the source product.
        if (!this.isEditMode() || this.isCloneMode()) {
          await this.addProduct(values, continueCreate, close, hasSpecs);
        } else {
          await this.updateProduct(values, close);
        }
      })
      .catch((error) => {
        console.log(
          "🚀 ~ file: ProductEdit.js:1674 ~ ProductEdit ~ this.productForm.current.validateFields ~ error:",
          error
        );
      });
  };

  validateSpecsBeforeSave() {
    const propertyForm = this.propertyFormRef.current;
    if (!propertyForm?.validateSpecs) {
      return { valid: true };
    }
    return propertyForm.validateSpecs();
  }

  hasSkuFormData(values) {
    return Object.keys(values?.property_id || {}).length > 0;
  }

  effectiveHasSpecs(values) {
    return this.state.hasSpecs || this.hasSkuFormData(values);
  }

  getSpecContext() {
    const propertyForm = this.propertyFormRef.current;
    return {
      groups: propertyForm?.getGroups?.() || [],
      skuList: propertyForm?.getSkuList?.() || [],
      isZh: !this.i18n.isLocaleRTL(),
    };
  }

  getSkuPayloadFromValues(values) {
    const { groups, skuList, isZh } = this.getSpecContext();
    return buildSkuPayloadFromForm(groups, skuList, values, isZh);
  }

  applySkuPayloadToData(data, values, context = null) {
    const specContext = context || this.getSpecContext();
    const { groups, skuList, isZh } = specContext;
    const skuPayload = buildSkuPayloadFromFormOrValues(
      groups,
      skuList,
      values,
      isZh
    );
    if (!skuPayload) return data;

    data.property_id = skuPayload.property_id;
    data.property_price = skuPayload.property_price;
    data.property_quantity = skuPayload.property_quantity;
    if (Object.keys(skuPayload.property_product_no).length > 0) {
      data.property_product_no = skuPayload.property_product_no;
    }

    data.price = firstSkuPriceCents(values, groups, isZh) || data.price || 0;
    data.quantity = totalSkuQuantity(values, groups, isZh);

    return data;
  }

  async persistSpecEntitiesForProduct(productId) {
    const propertyForm = this.propertyFormRef.current;
    if (!propertyForm?.persistSpecEntities) {
      throw new Error(
        this.i18n.t("property.selectGroup") +
          " (ProductPropertyForm ref not ready)"
      );
    }
    await propertyForm.persistSpecEntities(productId);
  }

  onFooterDeleteClick = () => {
    this.showModal(
      this.i18n.t("productEdit.delete"),
      Base.Modal.confirm,
      () => {
        this.deleteProduct();
      }
    );
  };

  onFooterCancelClick = () => {
    this.goToProductList();
  };

  /* server methods */

  async addProduct(values, continueCreate = false, close = true, hasSpecs = null) {
    if (this.state.updatingProduct) return;

    let token = this.getUserToken();
    if (!token) return;

    const savingWithSpecs = hasSpecs ?? this.effectiveHasSpecs(values);
    const specContext = this.getSpecContext();

    let api = this.api.product.customAdd;
    let priceCents = values.price * 100;
    let quantity = values.quantity;

    if (savingWithSpecs) {
      priceCents = firstSkuPriceCents(values, specContext.groups, specContext.isZh);
      quantity = totalSkuQuantity(values, specContext.groups, specContext.isZh);
    }

    const useImageDetail = this.isUseImageDetail();
    let apiConfig = {
      token,
      data: {
        category_id: values.category_id,
        is_agent_level_product: !!values.is_agent_level_product,
        product_no: values.product_no,
        zh_name: values.zh_name,
        ug_name: values.ug_name,
        zh_detail_description: values.zh_detail_description,
        ug_detail_description: values.ug_detail_description,
        barcode: values.barcode,
        pack_barcode:
          this.isMultiUnitEnabled() && values.has_pack_unit
            ? values.pack_barcode || ""
            : "",
        purchase_price: values.purchase_price * 100,
        price: priceCents,
        ship_price: values.ship_price * 100,
        max_buy_limit: Number(values.max_buy_limit) || 0,
        min_buy_limit: Number(values.min_buy_limit) || 0,
        quantity: quantity,
        pack_price: (values.pack_price || 0) * 100,
        pack_count: values.pack_count,
        status: values.status,
        order: values.order,
        recommended: values.recommend,
        images: [],
        is_free_shipping: values.is_free_shipping,
      },
    };

    if (useImageDetail) {
      apiConfig.data.detail_images = detailImagesToPaths(values.detail_images);
    } else {
      apiConfig.data.ug_description = values.ug_description
        ? values.ug_description.toHTML()
        : null;
      apiConfig.data.zh_description = values.zh_description
        ? values.zh_description.toHTML()
        : null;
    }

    this.applyPackUnitFields(apiConfig.data, values);
    Object.assign(
      apiConfig.data,
      this.buildAgentLevelCommissionPayload(values, this.state.selectedLevels)
    );

    // Do not attach SKU rows on create: draft/clone props have no ids yet, so codes
    // would be name-based and fail to reload. Persist groups first, then update SKUs.
    if (savingWithSpecs) {
      apiConfig.data.price = priceCents || 0;
      apiConfig.data.quantity = quantity || 0;
    }

    try {
      let imagePaths = [];
      let valueImages = values.images;
      let hasImages = valueImages && valueImages.length > 0;
      if (hasImages) {
        let images = valueImages.map((image, index) => {
          return {
            file: image.originFileObj,
            blob: image.thumbUrl,
          };
        });
        imagePaths = await this.uploadImages(images);
      }
      apiConfig.data.images = imagePaths;

      this.showMessage(
        this.i18n.t("productEdit.creatingProduct"),
        Base.Message.loading
      );
      this.setState({ updatingProduct: true });

      let res = await api.run(apiConfig);

      if (!this.mounted) return;

      if (res.result) {
        const newProductId = res.data?.id;
        let savedProduct = res.data;

        if (savingWithSpecs && newProductId) {
          try {
            const { groups, skuList, isZh } = this.getSpecContext();
            const snapshot = {
              ...this.propertyFormRef.current?.getSkuSnapshotByLabel?.(),
              ...snapshotSkuValuesByLabelFromGroups(groups, values, isZh),
            };
            await this.persistSpecEntitiesForProduct(newProductId);
            const rebuilt =
              (await this.propertyFormRef.current?.rebuildSkuFormFields?.(
                newProductId,
                snapshot
              )) || null;
            const rebuiltGroups =
              rebuilt?.groups ||
              this.propertyFormRef.current?.getGroups?.() ||
              groups;
            const rebuiltSkuList =
              rebuilt?.skuList ||
              this.propertyFormRef.current?.getSkuList?.() ||
              skuList;
            const rebuiltIsZh = rebuilt?.isZh ?? isZh;
            const skuFormValues =
              rebuilt?.formValues ||
              buildSkuFormValues(
                rebuiltGroups,
                rebuiltSkuList,
                rebuiltIsZh,
                snapshot
              );
            const skuValuesForUpdate = pickSkuFormValues(skuFormValues, values);
            const updateApi = this.api.product.customUpdate;
            const updateConfig = {
              token,
              path: { id: newProductId },
              data: {
                category_id: values.category_id,
                zh_name: values.zh_name,
                ug_name: values.ug_name,
                purchase_price: values.purchase_price * 100,
                pack_price: (values.pack_price || 0) * 100,
                pack_count: values.pack_count,
                ship_price: values.ship_price * 100,
                status: values.status,
                order: values.order,
                recommended: values.recommend,
                images: imagePaths,
                is_free_shipping: values.is_free_shipping,
              },
            };
            this.applyPackUnitFields(updateConfig.data, values);
            Object.assign(
              updateConfig.data,
              this.buildAgentLevelCommissionPayload(
                values,
                this.state.selectedLevels
              )
            );
            this.applySkuPayloadToData(updateConfig.data, skuValuesForUpdate, {
              groups: rebuiltGroups,
              skuList: rebuiltSkuList,
              isZh: rebuiltIsZh,
            });
            const skuRes = await updateApi.run(updateConfig);
            if (!skuRes.result) {
              this.showMessage(skuRes.message, Base.Message.error);
              return;
            }
            if (skuRes.data) {
              savedProduct = skuRes.data;
            }
          } catch (specError) {
            this.showMessage(
              specError.message || this.i18n.t("base.api.errUnknown"),
              Base.Message.error
            );
            return;
          }
        }

        this.showMessage(
          this.i18n.t("productEdit.creatingProductSuccess"),
          Base.Message.success
        );
        if (continueCreate) {
          this.productForm.current.resetFields();
          this.productForm.current.setFieldsValue({
            zh_description: EditorState.createFrom(""),
            ug_description: EditorState.createFrom(""),
          });
          this.setState({ hasSpecs: false, activeKey: "1" });
        } else if (this.isCloneMode() && newProductId) {
          // Leave clone mode and open the new product for further edits.
          const {
            location: { pathname, search, state },
            history,
          } = this.props;
          history.replace({
            pathname: pathname + search,
            state: {
              ...state,
              edit: true,
              clone: false,
              product: savedProduct || { ...res.data, id: newProductId },
            },
          });
          this.setState({ hasSpecs: savingWithSpecs });
          if (close) {
            this.goToProductList();
          }
        } else {
          if (close) {
            this.goToProductList();
          }
        }
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ updatingProduct: false });
    }
  }

  async updateProduct(values, close = true) {
    console.log(
      "🚀 ~ file: ProductEdit.js:1816 ~ ProductEdit ~ updateProduct ~ values:",
      values
    );
    if (this.state.updatingProduct) return;

    let product = this.getEditProduct();
    if (!product) return;

    let token = this.getUserToken();
    if (!token) return;

    let api = this.api.product.customAdd;

    let priceCents = values.price != null ? values.price * 100 : product.price;
    let quantity = values.quantity != null ? values.quantity : product.quantity;

    if (this.effectiveHasSpecs(values)) {
      const { groups, isZh } = this.getSpecContext();
      priceCents = firstSkuPriceCents(values, groups, isZh) || priceCents || 0;
      quantity = totalSkuQuantity(values, groups, isZh);
    }

    const useImageDetail = this.isUseImageDetail();
    let apiConfig = {
      token: token,
      path: {},
      data: {
        category_id: values.category_id,
        zh_name: values.zh_name,
        ug_name: values.ug_name,
        zh_detail_description: values.zh_detail_description,
        ug_detail_description: values.ug_detail_description,
        barcode: values.barcode,
        pack_barcode:
          this.isMultiUnitEnabled() && values.has_pack_unit
            ? values.pack_barcode || ""
            : "",
        purchase_price: values.purchase_price * 100,
        price: priceCents,
        quantity: quantity,
        pack_price: values.pack_price * 100,
        pack_count: values.pack_count,
        ship_price: values.ship_price * 100,
        max_buy_limit: Number(values.max_buy_limit) || 0,
        min_buy_limit: Number(values.min_buy_limit) || 0,
        status: values.status,
        order: values.order,
        recommended: values.recommend,
        images: [],
        video: "",
        commission_percent: values.commission_percent,
        parent_commission_percent: values.parent_commission_percent,
        is_free_shipping: values.is_free_shipping,
        is_agent_level_product: !!values.is_agent_level_product,
        valid_date: values.valid_date + values.validUnion,
        manufactured_date: values.manufactured_date
          ? `${values.manufactured_date.$y}-${
              values.manufactured_date.$M + 1
            }-${values.manufactured_date.$D}`
          : null,
        recommended_price: values.recommended_price,
        basic_sales: values.basic_sales,
      },
    };

    if (useImageDetail) {
      apiConfig.data.detail_images = detailImagesToPaths(values.detail_images);
    } else {
      apiConfig.data.ug_description = values.ug_description
        ? values.ug_description.toHTML()
        : null;
      apiConfig.data.zh_description = values.zh_description
        ? values.zh_description.toHTML()
        : null;
    }

    Object.assign(
      apiConfig.data,
      this.buildAgentLevelCommissionPayload(values, this.state.selectedLevels)
    );

    this.applyPackUnitFields(apiConfig.data, values);

    if (this.isEditMode() && !this.isCloneMode()) {
      api = this.api.product.customUpdate;
      if (this.isJdShop()) {
        api = this.api.product.jdCustomUpdate;
      } else {
        apiConfig.data.product_no = values.product_no;
      }
      apiConfig.path.id = product.id;
    }

    try {
      // Guard: clone must never persist specs onto the source product.
      if (this.isCloneMode()) {
        await this.addProduct(values, false, close, this.effectiveHasSpecs(values));
        return;
      }

      if (this.effectiveHasSpecs(values) && product?.id) {
        const { groups, skuList, isZh } = this.getSpecContext();
        const snapshot =
          this.propertyFormRef.current?.getSkuSnapshotByLabel?.() || {};
        await this.persistSpecEntitiesForProduct(product.id);
        const rebuilt =
          (await this.propertyFormRef.current?.rebuildSkuFormFields?.(
            product.id,
            snapshot
          )) || null;
        const rebuiltGroups = rebuilt?.groups || groups;
        const rebuiltSkuList = rebuilt?.skuList || skuList;
        const rebuiltIsZh = rebuilt?.isZh ?? isZh;
        const skuFormValues =
          rebuilt?.formValues ||
          buildSkuFormValues(rebuiltGroups, rebuiltSkuList, rebuiltIsZh, snapshot);
        const skuValuesForUpdate = pickSkuFormValues(skuFormValues, values);
        this.applySkuPayloadToData(apiConfig.data, skuValuesForUpdate, {
          groups: rebuiltGroups,
          skuList: rebuiltSkuList,
          isZh: rebuiltIsZh,
        });
        apiConfig.data.price =
          firstSkuPriceCents(skuValuesForUpdate, rebuiltGroups, rebuiltIsZh) ||
          apiConfig.data.price;
        apiConfig.data.quantity = totalSkuQuantity(
          skuValuesForUpdate,
          rebuiltGroups,
          rebuiltIsZh
        );
      } else if (this.effectiveHasSpecs(values)) {
        this.applySkuPayloadToData(apiConfig.data, values);
      }

      let imagePaths = [];
      let valueImages = values.images;
      let hasImages = valueImages && valueImages.length > 0;
      if (hasImages) {
        let images = valueImages
          .filter((image, index) => {
            if (!image.status) {
              return image;
            }
            return null;
          })
          .map((image, index) => {
            return {
              file: image.originFileObj,
              blob: image.thumbUrl,
            };
          });
        if (images && images.length > 0) {
          imagePaths = await this.uploadImages(images);
        }
        imagePaths = valueImages
          .filter((image, index) => {
            if (image.status) {
              return image;
            }
            return null;
          })
          .map((image, index) => {
            return image.path;
          })
          .concat(imagePaths);
      }
      apiConfig.data.images = imagePaths;

      const videoPaths = getUploadedVideoPaths(values.video);

      if (videoPaths) {
        apiConfig.data.video = videoPaths[0];
      }
      if (this.state.videoDeleted) {
        apiConfig.data.video = "";
      }

      this.showMessage(
        this.i18n.t("productEdit.updatingProductSuccess"),
        Base.Message.loading
      );
      this.setState({ updatingProduct: true });

      let res = await api.run(apiConfig);

      if (!this.mounted) return;

      if (res.result) {
        this.showMessage(
          this.i18n.t("productEdit.updatingProductSuccess"),
          Base.Message.success
        );
        if (close) {
          this.goToProductList();
        } else {
          const {
            location: { pathname, search, state },
            history,
          } = this.props;
          history.replace({
            pathname: pathname + search,
            state: {
              ...state,
              product: {
                ...res.data,
              },
            },
          });
        }
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ updatingProduct: false });
    }
  }

  async deleteProduct() {
    if (this.state.updatingProduct) return;

    let product = this.getEditProduct();
    if (!product) return;

    let token = this.getUserToken();
    if (!token) return;

    let api = this.isJdShop()
      ? this.api.product.jdCustomDelete
      : this.api.product.customDelete;
    let apiConfig = {
      token: token,
      path: {
        id: product.id,
      },
    };

    try {
      this.showMessage(
        this.i18n.t("productEdit.updatingProductSuccess"),
        Base.Message.loading
      );
      this.setState({ updatingProduct: true });

      let res = await api.run(apiConfig);

      if (!this.mounted) return;

      if (res.result) {
        this.showMessage(res.message, Base.Message.success);
        this.goToProductList();
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ updatingProduct: false });
    }
  }

  async uploadImages(images) {
    if (this.state.uploadingImages) throw new Error("uploading");

    if (!images && images.length <= 0) throw new Error("invalid images");

    let token = this.getUserToken();
    if (!token) throw new Error("no user token");

    let apiConfig = {
      token: token,
      uploadFiles: images,
      uploadType: "images",
    };

    this.showMessage(
      this.i18n.t("productEdit.uploadingImages"),
      Base.Message.loading
    );
    this.setState({ uploadingImages: true });
    try {
      let res = await this.api.common.uploadImages.run(apiConfig);
      if (res.result) {
        let data = res.data;
        return data.map((item) => item.path);
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      throw error;
    } finally {
      if (!this.mounted) return;
      this.setState({ uploadingImages: false });
    }
  }

  getJdProductImg = async () => {
    let token = this.getUserToken();
    if (!token) throw new Error("no user token");

    let apiConfig = {
      token,
      path: {
        sku_id: this.state.editProduct.jd_sku,
      },
    };

    this.setState({ fetchingImg: "img" });

    try {
      let res = await this.api.product.jdProductImg.run(apiConfig);
      if (res.result) {
        this.productForm.current.setFieldsValue({
          images: res.data.map((i, indx) => ({
            name: `name${indx}`,
            status: "done",
            uid: -indx,
            ...i,
          })),
        });
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      throw error;
    } finally {
      if (!this.mounted) return;
      this.setState({ fetchingImg: null });
    }
  };

  getJdProductDescription = async () => {
    let token = this.getUserToken();
    if (!token) throw new Error("no user token");

    let apiConfig = {
      token,
      path: {
        sku_id: this.state.editProduct.jd_sku,
      },
    };

    this.setState({ fetchingImg: "disc" });

    try {
      let res = await this.api.product.jdProductDesc.run(apiConfig);
      if (res.result) {
        this.productForm.current.setFieldsValue({
          zh_description: EditorState.createFrom(res.data),
          ug_description: EditorState.createFrom(res.data),
        });
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      throw error;
    } finally {
      if (!this.mounted) return;
      this.setState({ fetchingImg: null });
    }
  };

  /* custom methods */

  goToProductList() {
    this.props.history.goBack();
  }

  getCategories() {
    const list = this.getFromRouterState("categories") || [];
    return nestMerchantProductCategories(Array.isArray(list) ? [...list] : list);
  }

  getEditShow() {
    return this.getFromRouterState("show");
  }

  isEditMode() {
    return this.getFromRouterState("edit");
  }

  isCloneMode() {
    return this.getFromRouterState("clone");
  }

  getEditProduct() {
    return this.getFromRouterState("product");
  }

  hasEditProductSku() {
    let product = this.getEditProduct();
    return product ? product.sku_id > 0 : false;
  }

  getDefaultCategory() {
    let cats = this.getCategories();

    let cat = null;
    if (cats && cats.length > 0) {
      const flat = flattenProductCategoriesForLookup(cats);
      let stateCatId = this.getFromRouterState("currentCat");
      if (stateCatId) {
        cat = flat.find((c) => c.id === stateCatId);
      }

      if (!cat && this.isEditMode() && this.getEditProduct()) {
        const productCatId = this.getEditProduct().category_id;
        cat = flat.find((c) => c.id === productCatId);
      }

      if (!cat) {
        cat = cats[0];
      }
    }

    return cat ? `${cat.id}` : -1;
  }

  getDefaultNumber() {
    let number = "";

    if (this.isEditMode() && this.getEditProduct()) {
      number = this.getEditProduct().product_no;
    }

    return number;
  }

  getDefaultValidDate() {
    let number = "";

    if (this.isEditMode() && this.getEditProduct()) {
      number = this.getEditProduct().valid_date;
    }

    return number;
  }

  getDefaultRecommendedPrice() {
    let number = "";

    if (this.isEditMode() && this.getEditProduct()) {
      number = this.getEditProduct().recommended_price;
    }

    return number;
  }

  getDefaultBasicSales() {
    let number = "";

    if (this.isEditMode() && this.getEditProduct()) {
      number = this.getEditProduct().basic_sales;
    }

    return number;
  }

  isVideoLessThan10MB(image) {
    return image.size / 1024 / 1024 < 20;
  }

  getDefaultVideo() {
    let video = [];

    if (this.isEditMode() && this.getEditProduct()) {
      let showVideo = this.getEditProduct().video;

      if (showVideo) {
        video.push({
          uid: -1 * (1 + 1),
          name: `video${1 + 1}`,
          status: "done",
          url: showVideo && showVideo.url,
          path: showVideo && showVideo.path,
        });
      }
    }

    return video;
  }

  getDefaultBarcode() {
    let number = "";

    if (this.isEditMode() && this.getEditProduct()) {
      number = this.getEditProduct().barcode;
    }

    return number;
  }

  getDefaultPackBarcode() {
    if (this.isEditMode() && this.getEditProduct()) {
      return this.getEditProduct().pack_barcode || "";
    }
    return "";
  }

  getDefaultName() {
    let name = "";

    if (this.isEditMode() && this.getEditProduct()) {
      name = this.getEditProduct().zh_name;
    }

    return name;
  }

  getDefaultUgName() {
    let name = "";

    if (this.isEditMode() && this.getEditProduct()) {
      name = this.getEditProduct().ug_name;
    }

    return name;
  }

  getDetailDesc() {
    let desc = "";

    if (this.isEditMode() && this.getEditProduct()) {
      desc = this.getEditProduct().zh_detail_description;
    }

    return desc;
  }

  getDetailUgDesc() {
    let desc = "";

    if (this.isEditMode() && this.getEditProduct()) {
      desc = this.getEditProduct().ug_detail_description;
    }

    return desc;
  }

  getDefaultImages() {
    let images = [];

    if (this.isEditMode() && this.getEditProduct()) {
      let productImages = this.getEditProduct().images;
      if (productImages && productImages.length > 0) {
        for (let i = 0; i < productImages.length; i++) {
          images.push({
            uid: -1 * (i + 1),
            name: `image${i + 1}`,
            status: "done",
            url: productImages[i].url,
            path: productImages[i].path,
          });
        }
      }
    }

    return images;
  }

  getDefaultDetailImages() {
    if (!this.isEditMode() || !this.getEditProduct()) {
      return [];
    }
    const list = this.getEditProduct().detail_images;
    if (!Array.isArray(list) || !list.length) {
      return [];
    }
    return list
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") {
          return { path: item, url: item };
        }
        const path = item.path || item.file_path || item.url;
        const url = item.url || path;
        if (!path) return null;
        return { path, url };
      })
      .filter(Boolean);
  }

  getDefaultDescription() {
    let description = null;

    if (this.isEditMode() && this.getEditProduct()) {
      description = EditorState.createFrom(
        this.getEditProduct().zh_description
      );
    }

    return description;
  }

  getDefaultUgDescription() {
    let description = null;

    if (this.isEditMode() && this.getEditProduct()) {
      description = EditorState.createFrom(
        this.getEditProduct().ug_description
      );
    }

    return description;
  }

  // getDefaultTag() {
  // 	let tag = ['desert', 'shala'];

  // 	if (this.isEditMode() && this.getEditProduct()) {
  // 		tag = ['zhushi', 'gaifan'];
  // 	}

  // 	return tag;
  // }

  getDefaultInPrice() {
    let price = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      price = this.util.getPrice(this.getEditProduct().purchase_price || 0);
    }

    return price;
  }

  getDefaultShipPrice() {
    let price = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      price = this.util.getPrice(this.getEditProduct().ship_price || 0);
    }

    return price;
  }

  getDefaultFreeShipping() {
    if (this.isEditMode() && this.getEditProduct()) {
      return this.getEditProduct().is_free_shipping;
    }
    return false;
  }
  getDefaultAgentLevelProduct() {
    if (this.isEditMode() && this.getEditProduct()) {
      return !!this.getEditProduct().is_agent_level_product;
    }
    return false;
  }

  getDefaultMaxBuyLimit() {
    if (this.isEditMode() && this.getEditProduct()) {
      return Number(this.getEditProduct().max_buy_limit) || 0;
    }
    return 0;
  }

  getDefaultMinBuyLimit() {
    if (this.isEditMode() && this.getEditProduct()) {
      return Number(this.getEditProduct().min_buy_limit) || 0;
    }
    return 0;
  }

  getDefaultOutPrice() {
    let price = 0;
    let product = this.getEditProduct();
    if (this.isEditMode() && product) {
      let tmp = product.is_flash_sale ? product.original_price : product.price;
      price = this.util.getPrice(tmp);
    }
    return price;
  }

  getDefaultCommission() {
    let commission = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      commission = this.getEditProduct().commission_percent;
    }

    return commission;
  }

  getDefaultParentCommission() {
    let commission = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      commission = this.getEditProduct().parent_commission_percent;
    }

    return commission;
  }

  getDefaultQuantity() {
    let quantity = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      quantity = this.getEditProduct().quantity;
    }

    return quantity;
  }

  getDefaultPackPrice() {
    let packPrice = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      packPrice = this.util.getPrice(this.getEditProduct().pack_price);
    }

    return packPrice;
  }

  getDefaultPackCount() {
    let count = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      count = this.getEditProduct().pack_count;
    }

    return count;
  }

  getDefaultUnitId() {
    if (this.isEditMode() && this.getEditProduct()) {
      return this.getEditProduct().unit_id || undefined;
    }
    return undefined;
  }

  getDefaultPackUnitId() {
    if (this.isEditMode() && this.getEditProduct()) {
      return this.getEditProduct().pack_unit_id || undefined;
    }
    return undefined;
  }

  getDefaultPackSize() {
    if (this.isEditMode() && this.getEditProduct()) {
      const size = this.getEditProduct().pack_size;
      return size >= 2 ? size : undefined;
    }
    return undefined;
  }

  getDefaultPackUnitPrice() {
    if (this.isEditMode() && this.getEditProduct()) {
      const cents = this.getEditProduct().pack_unit_price;
      if (cents != null && cents > 0) {
        return this.util.getPrice(cents);
      }
      // Fallback: bottle price × pack size when pack price not set yet
      const product = this.getEditProduct();
      if (product.pack_size >= 2 && product.price) {
        return this.util.getPrice(product.price * product.pack_size);
      }
    }
    return undefined;
  }

  getDefaultHasPackUnit() {
    if (this.isEditMode() && this.getEditProduct()) {
      const product = this.getEditProduct();
      return !!(product.pack_unit_id && product.pack_size >= 2);
    }
    return false;
  }

  getDefaultStatus() {
    let status = ProductEdit.Status[0].value;

    if (this.isEditMode() && this.getEditProduct()) {
      status = this.getEditProduct().status;
    }

    return status;
  }

  getDefaultOrder() {
    let count = 0;

    if (this.isEditMode() && this.getEditProduct()) {
      count = this.getEditProduct().order;
    }

    return count;
  }

  getDefaultSuggestion() {
    let count = ProductEdit.Suggestion[0].value;
    if (this.isEditMode() && this.getEditProduct()) {
      count = this.getEditProduct().recommended ? 1 : 0;
    }
    return count;
  }

  isImageTypeValid(image) {
    const types = ["image/jpeg", "image/png"];
    return types.indexOf(image.type) !== -1;
  }

  isImageLessThan1MB(image) {
    return image.size / 1024 / 1024 < 1;
  }
}

export default connect(
  (state) => {
    return {
      user: getUserInfo(state),
      images: getMedias(state),
      settings: getMerchantSettings(state),
    };
  },
  { addMedia, removeMedia }
)(ProductEdit);
